import { useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import { Textbox } from 'fabric';
import { cn } from '@/lib/utils';
import { STICKER_PACKS, searchStickers } from './utils/stickers';

/**
 * Sticker picker panel — category tabs + grid of emoji stickers.
 * Adds selected emoji as a large text object on the canvas.
 */
export const StickerPanel = ({ fabricRef, onModified, isDark }) => {
  const [activePack, setActivePack] = useState(STICKER_PACKS[0].key);
  const [searchQuery, setSearchQuery] = useState('');

  const addStickerToCanvas = useCallback((emoji, label) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const sticker = new Textbox(emoji, {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 120,
      textAlign: 'center',
      width: 160,
      editable: false,
    });

    sticker.set({
      cornerSize: 12,
      touchCornerSize: 40,
      cornerPadding: 10,
      cornerColor: '#FFFFFF',
      cornerStrokeColor: '#000000',
      borderColor: '#FFFFFF',
      transparentCorners: false,
    });
    sticker.__id = `sticker-${Date.now()}`;
    sticker.__layerName = `Sticker: ${label}`;
    sticker.__isSticker = true;

    canvas.add(sticker);
    canvas.setActiveObject(sticker);
    canvas.renderAll();
    onModified?.();
  }, [fabricRef, onModified]);

  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const activeBg = isDark ? 'bg-white/10' : 'bg-gray-100';
  const hoverBg = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-100';

  const searchResults = searchQuery.length >= 2 ? searchStickers(searchQuery) : null;
  const currentPack = STICKER_PACKS.find(p => p.key === activePack);

  return (
    <div className="p-3 space-y-2.5">
      {/* Search */}
      <div className={cn("flex items-center gap-2 px-3 py-2 rounded-sm", inputBg)}>
        <Search className={cn("h-4 w-4 shrink-0", mutedClass)} />
        <input
          type="text"
          placeholder="Search stickers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            "bg-transparent text-sm outline-none w-full placeholder:text-current",
            mutedClass
          )}
        />
      </div>

      {/* Category tabs — horizontal scroll */}
      {!searchResults && (
        <div className={cn("flex gap-1.5 overflow-x-auto border-b pb-1.5 -mx-1 px-1", borderClass)}>
          {STICKER_PACKS.map((pack) => (
            <button
              key={pack.key}
              onClick={() => setActivePack(pack.key)}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-display tracking-wider uppercase whitespace-nowrap rounded-sm transition-colors shrink-0",
                activePack === pack.key ? cn(textClass, activeBg) : mutedClass,
              )}
            >
              <span className="text-sm">{pack.icon}</span>
              {pack.label}
            </button>
          ))}
        </div>
      )}

      {/* Sticker grid — 5 columns, larger touch targets */}
      <div className="grid grid-cols-5 gap-1 max-h-36 overflow-y-auto">
        {searchResults ? (
          searchResults.length > 0 ? (
            searchResults.map((sticker) => (
              <button
                key={`${sticker.packKey}-${sticker.key}`}
                onClick={() => addStickerToCanvas(sticker.emoji, sticker.label)}
                className={cn(
                  "flex items-center justify-center p-2 rounded-sm transition-colors aspect-square",
                  hoverBg
                )}
                title={sticker.label}
              >
                <span className="text-3xl">{sticker.emoji}</span>
              </button>
            ))
          ) : (
            <div className={cn("col-span-5 text-center py-4 text-xs", mutedClass)}>
              No stickers found
            </div>
          )
        ) : (
          currentPack?.stickers.map((sticker) => (
            <button
              key={sticker.key}
              onClick={() => addStickerToCanvas(sticker.emoji, sticker.label)}
              className={cn(
                "flex items-center justify-center p-2 rounded-sm transition-colors aspect-square",
                hoverBg
              )}
              title={sticker.label}
            >
              <span className="text-3xl">{sticker.emoji}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
